import type { BuilderConfig } from '~/lib/builder-types';
import type { Feature, Spec } from '~/lib/db/schema';
import type {
  DepthLayer,
  FeatureDefinition,
} from '~/lib/feature-modules/types';

export interface FeatureWithDefinition extends Feature {
  definition: FeatureDefinition;
}

export function exportAsPrompt(
  spec: Spec,
  config: BuilderConfig,
  featureList: FeatureWithDefinition[],
): string {
  const enabledFeatures = featureList.filter(
    (f) => config.features[f.slug]?.enabled,
  );
  const disabledFeatures = featureList.filter(
    (f) => !config.features[f.slug]?.enabled,
  );

  const lines: string[] = [
    'You are implementing the following software specification. Build exactly what is described — no more, no less.',
    '',
    `# ${spec.name}`,
    '',
    spec.description,
    '',
    '---',
    '',
    `## Enabled features (${enabledFeatures.length})`,
  ];

  for (const feature of enabledFeatures) {
    const featureConfig = config.features[feature.slug];
    lines.push('');
    lines.push(`### ${feature.name} · ${feature.complexity} complexity`);
    lines.push(feature.description);

    for (const group of feature.definition.optionGroups) {
      const selected = featureConfig?.options[group.id];
      if (!selected) continue;
      const selectedIds = Array.isArray(selected) ? selected : [selected];
      const selectedOptions = group.options.filter((o) =>
        selectedIds.includes(o.id),
      );
      if (selectedOptions.length === 0) continue;
      lines.push('');
      lines.push(
        `**${group.label}:** ${selectedOptions.map((o) => o.label).join(' + ')}`,
      );
    }

    if (feature.definition.tradeoffs.length > 0) {
      lines.push('');
      lines.push(
        `_Tradeoffs: ${feature.definition.tradeoffs.map((t) => t.condition).join('; ')}_`,
      );
    }
  }

  const featuresWithDepth = enabledFeatures.filter(
    (f) => f.definition.depthLayers && f.definition.depthLayers.length > 0,
  );

  if (featuresWithDepth.length > 0) {
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push('## Implementation depth');

    for (const feature of featuresWithDepth) {
      lines.push('');
      lines.push(`### ${feature.name}`);
      renderDepthLayers(lines, feature.definition.depthLayers!, 0);
    }
  }

  const featuresWithDecisions = enabledFeatures.filter(
    (f) => f.definition.decisionPoints.length > 0,
  );

  if (featuresWithDecisions.length > 0) {
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push('## Key decisions');

    for (const feature of featuresWithDecisions) {
      for (const dp of feature.definition.decisionPoints) {
        lines.push('');
        lines.push(`**${feature.name} — ${dp.question}**`);
        lines.push(`- If yes: ${dp.ifYes}`);
        lines.push(`- If no: ${dp.ifNo}`);
      }
    }
  }

  if (disabledFeatures.length > 0) {
    lines.push('');
    lines.push('---');
    lines.push('');
    lines.push('## Excluded features (do not build)');
    lines.push('');
    for (const feature of disabledFeatures) {
      lines.push(`- ${feature.name}`);
    }
  }

  // Build dependency-sorted list for implementation order
  const sorted: FeatureWithDefinition[] = [];
  const remaining = [...enabledFeatures];
  const placed = new Set<string>();

  let maxIter = enabledFeatures.length * enabledFeatures.length + 1;
  while (remaining.length > 0 && maxIter-- > 0) {
    const idx = remaining.findIndex((f) =>
      f.definition.dependencies.every(
        (dep) => placed.has(dep) || !config.features[dep]?.enabled,
      ),
    );
    if (idx === -1) {
      sorted.push(...remaining);
      break;
    }
    const [f] = remaining.splice(idx, 1);
    sorted.push(f);
    placed.add(f.slug);
  }

  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push('## Implementation order');
  lines.push('');
  lines.push('Build in this dependency order:');
  sorted.forEach((f, i) => {
    lines.push(`${i + 1}. ${f.name}`);
  });

  lines.push('');
  lines.push('---');
  lines.push('');
  lines.push(`*Preset: ${config.preset} · Generated by Specly*`);

  return lines.join('\n');
}

function renderDepthLayers(
  lines: string[],
  layers: DepthLayer[],
  indent: number,
): void {
  const prefix = '  '.repeat(indent);
  for (const layer of layers) {
    lines.push(`${prefix}- **${layer.label}**: ${layer.summary}`);
    if (layer.approaches && layer.approaches.length > 0) {
      for (const approach of layer.approaches) {
        lines.push(`${prefix}  - _${approach.label}_: ${approach.description}`);
      }
    }
    if (layer.children && layer.children.length > 0) {
      renderDepthLayers(lines, layer.children, indent + 1);
    }
  }
}

export function exportAsJson(
  spec: Spec,
  config: BuilderConfig,
  featureList: FeatureWithDefinition[],
): string {
  const enabledFeatures = Object.entries(config.features)
    .filter(([, fc]) => fc.enabled)
    .map(([slug, fc]) => {
      const feature = featureList.find((f) => f.slug === slug);
      return {
        slug,
        name: feature?.name ?? slug,
        options: fc.options,
      };
    });

  return JSON.stringify(
    {
      spec: {
        id: spec.id,
        name: spec.name,
        slug: spec.slug,
        category: spec.category,
      },
      preset: config.preset,
      features: enabledFeatures,
    },
    null,
    2,
  );
}
